import type { ApiClient } from "@hakili/api-client";
import { messageOf } from "@hakili/app-shell";
import type { CabinetDemandFactsAnswer, CabinetEntry, CabinetEntryAnswer } from "@hakili/contracts";
import { formatDate, formatDateTime, formatMoney, formatQuantity, t } from "@hakili/i18n";
import { CheckCircle2, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

/**
 * `round C5-2`, `M3`: **the entry, the drawing's `Controle`** (`hakili-expert.jsx`),
 * on the cabinet's space.
 *
 * The vehicle's facts as Hakili holds them , NO asked price on this surface
 * (decision A) , what the expert verifies on arriving (the chassis and the
 * plate against the carte grise, character by character; the odometer read
 * against the declared mileage, a gap a FACT and never a tick by itself), the
 * grid's families with their help, the value entered BLIND, the observations,
 * the photographs as the expert's named views by the documents' own doors
 * with provenance `cabinet`, the attestation at the foot. NO verdict during
 * entry (decision B): the verdict is answered by the door only once the
 * responsable's visa has sent the report, and the screen reads it then.
 *
 * The send guard is said in words beside the control, never mute.
 */

/** The cabinet's facts on one demand, exactly as `GET /cabinet/demands/{id}` sends them (the contract). */
export type EntryFacts = CabinetDemandFactsAnswer;

/** The expert's entry as the doors send it (the contract). */
export type EntryState = CabinetEntry;

/**
 * The expert's eight advised views (`VUES_CONSEILLEES_EXPERT`): the KEY is the
 * wire value stored with the document, the label is the catalogue's (`OD-23`,
 * « a photograph view's label is copy, and its key is not »).
 */
export const EXPERT_VIEWS = [
  { key: "frontRight", label: "expert.vue.frontRight" },
  { key: "sideRight", label: "expert.vue.sideRight" },
  { key: "rear", label: "expert.vue.rear" },
  { key: "sideLeft", label: "expert.vue.sideLeft" },
  { key: "chassisNumber", label: "expert.vue.chassisNumber" },
  { key: "odometer", label: "expert.vue.odometer" },
  { key: "engine", label: "expert.vue.engine" },
  { key: "underbody", label: "expert.vue.underbody" },
] as const;
const fill = (key: Parameters<typeof t>[0], values: Record<string, string>): string =>
  Object.entries(values).reduce((text, [name, value]) => text.replace(`{${name}}`, value), t(key));

const VERDICT_WORDS: ReadonlyMap<string, string> = new Map([
  ["conforme", "expert.rapport.conforme"],
  ["ecartprix", "expert.rapport.ecartPrix"],
  ["nonconforme", "expert.rapport.nonConforme"],
]);

function Line({ label, value }: { readonly label: string; readonly value: string }): React.JSX.Element {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-900 tabular-nums">{value}</span>
    </div>
  );
}

function YesNo(props: {
  readonly value: boolean | null;
  readonly onChange: (value: boolean) => void;
  readonly testId: string;
  /** `LANE E`: a sent entry is read-only , the answers too. */
  readonly disabled?: boolean;
}): React.JSX.Element {
  const { value, onChange, testId, disabled = false } = props;
  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      {[true, false].map((one) => (
        <button
          key={String(one)}
          type="button"
          data-testid={`${testId}-${one ? "oui" : "non"}`}
          aria-pressed={value === one}
          disabled={disabled}
          onClick={() => {
            onChange(one);
          }}
          className={`rounded-xl border py-2.5 text-sm font-medium ${
            value === one
              ? one
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-rose-200 bg-rose-50 text-rose-600"
              : "border-gray-200 bg-white text-gray-600"
          }`}
        >
          {one ? t("expert.saisie.oui") : t("expert.saisie.non")}
        </button>
      ))}
    </div>
  );
}

/** The verdict, AFTER the send (decision B) , the drawing's `RapportEnvoye`. */
export function ReportSent(props: { readonly facts: EntryFacts }): React.JSX.Element {
  const { report, entry } = props.facts;
  if (report === null) return <></>;
  const key = VERDICT_WORDS.get(report.verdict);
  return (
    <div className="mb-3 px-5" data-testid="rapport-envoye">
      <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-sm font-semibold">{t("expert.rapport.verdictCalcule")}</h2>
          <span
            data-testid="rapport-verdict"
            data-verdict={report.verdict}
            className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${
              report.verdict === "conforme"
                ? "bg-emerald-50 text-emerald-700"
                : report.verdict === "ecartprix"
                  ? "bg-amber-50 text-amber-700"
                  : "bg-rose-50 text-rose-600"
            }`}
          >
            {key === undefined ? report.verdict : t(key)}
          </span>
        </div>
        <Line
          label={t("expert.rapport.bareme")}
          value={fill("expert.feuille.faitsSur", { n: String(report.points), m: String(report.totalPoints) })}
        />
        <Line label={t("expert.rapport.valeur")} value={formatMoney(report.estimatedValueAmount)} />
        <Line label={t("expert.rapport.envoyeLe")} value={formatDate(new Date(report.reportedAt))} />
        {entry?.enteredByName === undefined || entry.enteredByName === null ? null : (
          <Line label={t("expert.rapport.saisiPar")} value={entry.enteredByName} />
        )}
        <p className="mt-2 text-xs leading-relaxed text-gray-500">{t("expert.rapport.apresEnvoi")}</p>
      </div>
    </div>
  );
}

export function EntryForm(props: {
  readonly api: ApiClient;
  readonly demandId: string;
  readonly chair: "expert" | "responsable";
  /** The desk reads the entry and gives the visa; the phone writes it. */
  readonly readOnly?: boolean;
  readonly uploads?: typeof globalThis.fetch;
}): React.JSX.Element {
  const { api, demandId, chair, readOnly = false } = props;
  const uploads = props.uploads ?? globalThis.fetch.bind(globalThis);
  const [facts, setFacts] = useState<EntryFacts | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [chassisRead, setChassisRead] = useState("");
  const [chassisMatches, setChassisMatches] = useState<boolean | null>(null);
  const [plateRead, setPlateRead] = useState("");
  const [plateMatches, setPlateMatches] = useState<boolean | null>(null);
  const [carteGriseFound, setCarteGriseFound] = useState<boolean | null>(null);
  const [yearRead, setYearRead] = useState("");
  const [yearMatches, setYearMatches] = useState<boolean | null>(null);
  const [odometer, setOdometer] = useState("");
  const [observed, setObserved] = useState<ReadonlyMap<string, string | null>>(new Map());
  const [value, setValue] = useState("");
  const [observations, setObservations] = useState("");
  const [photos, setPhotos] = useState<readonly { view: string; documentId: string }[]>([]);
  const [attested, setAttested] = useState(false);
  const [help, setHelp] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [word, setWord] = useState<string | null>(null);
  // `LANE E`: a refusal is said as one , the server's own sentence, announced, never a quiet footnote.
  const [wordRefused, setWordRefused] = useState(false);

  const read = useCallback(async (): Promise<void> => {
    try {
      const answer = await api.request<EntryFacts>("/api/v1/cabinet/demands/{id}", "get", {
        params: { id: demandId },
      });
      setFacts(answer);
      const entry = answer.entry;
      if (entry !== null) {
        setChassisRead(entry.chassisNumberRead ?? "");
        setChassisMatches(entry.chassisMatches);
        setPlateRead(entry.plateRead ?? "");
        setPlateMatches(entry.plateMatches);
        setCarteGriseFound(entry.carteGriseFound);
        setYearRead(entry.vehicleYearRead === null ? "" : String(entry.vehicleYearRead));
        setYearMatches(entry.vehicleYearMatches);
        setOdometer(entry.odometerReadKm === null ? "" : String(entry.odometerReadKm));
        setObserved(new Map(entry.observed.map((one) => [one.code, one.detail])));
        setValue(entry.estimatedValueAmount === null ? "" : String(entry.estimatedValueAmount));
        setObservations(entry.observations);
        setPhotos(entry.photos);
        setAttested(entry.attestedAt !== null);
      }
    } catch (failure) {
      setRefusal(messageOf(failure, t("common.error.connexionEchouee")));
    }
  }, [api, demandId]);

  useEffect(() => {
    void read();
  }, [read]);

  if (refusal !== null) {
    return (
      <p role="alert" className="px-5 text-sm text-rose-600" data-testid="saisie-refus">
        {refusal}
      </p>
    );
  }
  if (facts === null) return <p className="px-5 text-sm text-gray-500">{t("common.state.chargement")}</p>;
  if (facts.report !== null) return <ReportSent facts={facts} />;

  const { demand, grid } = facts;
  const declared = demand.vehicleMileageKm;
  const odometerValue = odometer === "" ? null : Number(odometer);
  const gap = odometerValue === null ? null : odometerValue - declared;
  const valueOk = value !== "" && Number(value) > 0;
  // `LANE E`, the expert checklist (founder's ruling, 17 October, E-006).
  // « Non immatriculé » is what the dealer declared, or what the demand carries
  // when it carries no plate and no declaration , the words this screen's own
  // header prints. The server reads the same rule (`cabinet-entry.service.ts`).
  const declaredRegistered = demand.vehicleRegistrationDeclared ?? null;
  const unregistered =
    declaredRegistered === false || (declaredRegistered === null && demand.vehiclePlate === null);
  // The carte grise is the FIRST question; the châssis and the year hang on it.
  // Without one they are « sans objet » , the system's reading, nothing to
  // answer, never a « Non » and never a constat; the absence is recorded once,
  // as `carteGriseFound = false`. An entry kept before this order, with its
  // comparisons answered and the carte grise not, still shows them.
  const sansCarteGrise = carteGriseFound === false;
  const comparing =
    carteGriseFound === true ||
    (carteGriseFound === null && (chassisMatches !== null || yearMatches !== null || yearRead !== ""));
  // « N point(s) sans réponse : … » , every unanswered point, in the screen's
  // order, so the button never blocks mute.
  const missing: string[] = [];
  if (carteGriseFound === null) missing.push(t("expert.saisie.manqueCarteGrise"));
  if (chassisRead.trim() === "") missing.push(t("expert.saisie.manqueChassisLu"));
  if (carteGriseFound === true) {
    if (chassisMatches === null) missing.push(t("expert.saisie.manqueChassis"));
    if (yearRead.trim() === "") missing.push(t("expert.saisie.manqueAnneeLue"));
    if (yearMatches === null) missing.push(t("expert.saisie.manqueAnnee"));
  }
  if (plateMatches === null) missing.push(t("expert.saisie.manquePlaque"));
  else if (unregistered && !plateMatches && plateRead.trim() === "")
    missing.push(t("expert.saisie.manquePlaqueLue"));
  if (odometer === "") missing.push(t("expert.saisie.manqueCompteur"));
  if (!valueOk) missing.push(t("expert.saisie.manqueValeur"));
  if (!attested) missing.push(t("expert.saisie.manqueAttestation"));
  const guard =
    missing.length === 0
      ? null
      : fill(missing.length > 1 ? "expert.saisie.pointsSansReponse" : "expert.saisie.pointSansReponse", {
          n: String(missing.length),
          liste: missing.join(", "),
        });
  // `LANE E`: once sent (attested, not yet visaed or returned) the form is
  // read-only , the door refuses a resend too (`ENTRY_SENT`). The office
  // returning it clears the attestation, which opens the form again.
  const sentAt = facts.entry?.attestedAt ?? null;
  const sent = sentAt !== null && facts.entry?.status !== "vise";
  const frozen = readOnly || facts.entry?.status === "vise" || sent;
  // `LANE E`, M2: the constats are the expert's ticks , the grid's findings are made of them, and the verdict of those. « Sans objet » and a recorded discrepancy are not among them.
  const count = observed.size;

  const toggle = (code: string): void => {
    if (frozen) return;
    setObserved((current) => {
      const next = new Map(current);
      if (next.has(code)) next.delete(code);
      else next.set(code, null);
      return next;
    });
  };

  const save = (attest: boolean): void => {
    if (busy || frozen) return;
    setBusy(true);
    setWord(null);
    setWordRefused(false);
    void (async () => {
      try {
        await api.request<CabinetEntryAnswer>("/api/v1/cabinet/demands/{id}/entry", "put", {
          params: { id: demandId },
          body: {
            chassisNumberRead: chassisRead === "" ? null : chassisRead,
            // `LANE E`: « sans objet » is the system's reading, never sent as an answer.
            chassisMatches: sansCarteGrise ? null : chassisMatches,
            // A vehicle declared without a plate answers « aucune plaque, comme
            // déclaré » (Oui: no plate read; Non: the plate read, the discrepancy recorded).
            plateRead: unregistered && plateMatches !== false ? null : plateRead === "" ? null : plateRead,
            plateMatches,
            carteGriseFound,
            vehicleYearRead: sansCarteGrise || yearRead === "" ? null : Number(yearRead),
            vehicleYearMatches: sansCarteGrise ? null : yearMatches,
            odometerReadKm: odometerValue,
            observed: [...observed.entries()].map(([code, detail]) => ({ code, detail })),
            estimatedValueAmount: value === "" ? null : Number(value),
            observations,
            photos,
            attested: attest,
          },
        });
        // A sent entry is confirmed by the banner (with the time), not by a line of small print.
        setWord(attest ? null : t("expert.saisie.gardee"));
        await read();
      } catch (failure) {
        setWord(messageOf(failure, t("common.error.connexionEchouee")));
        setWordRefused(true);
      } finally {
        setBusy(false);
      }
    })();
  };

  const attach = (view: string, file: File): void => {
    if (frozen) return;
    void (async () => {
      try {
        const ticket = await api.request<{ id: string; url: string; headers: Record<string, string> }>(
          "/api/v1/documents/{ownerType}/{ownerId}/upload-ticket",
          "post",
          {
            params: { ownerType: "expertiseEntry", ownerId: demandId },
            body: { name: file.name, mimeType: file.type, sizeBytes: file.size, source: "cabinet" },
          },
        );
        const put = await uploads(ticket.url, { method: "PUT", headers: ticket.headers, body: file });
        if (!put.ok) throw new Error(t("common.error.connexionEchouee"));
        const confirmed = await api.request<{ documentId: string }>(
          "/api/v1/documents/{ownerType}/{ownerId}/confirm",
          "post",
          { params: { ownerType: "expertiseEntry", ownerId: demandId }, body: { ticketId: ticket.id } },
        );
        setPhotos((current) => [
          ...current.filter((one) => one.view !== view),
          { view, documentId: confirmed.documentId },
        ]);
      } catch (failure) {
        setWord(messageOf(failure, t("common.error.connexionEchouee")));
        setWordRefused(true);
      }
    })();
  };

  return (
    <div className="pb-28" data-testid="saisie">
      {/*
       * `ROUND S4-2-REFERENCE-DOCUMENT`, M1 (3): one line, EARLIER in the
       * flow , before the inspection fields, not after , so the paper asked
       * for later on the desk's own visa screen is never a surprise.
       */}
      <div className="mb-3 px-5">
        <p
          className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-2.5 text-xs leading-relaxed text-gray-500"
          data-testid="saisie-avis-rapport"
        >
          {t("expert.saisie.rapportAvis")}
        </p>
      </div>
      {facts.entry?.returnedNote === undefined || facts.entry.returnedNote === null ? null : (
        <div className="mb-3 px-5">
          <p
            className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-relaxed text-amber-700"
            data-testid="saisie-renvoyee"
          >
            {fill("expert.saisie.renvoyee", { note: facts.entry.returnedNote })}
          </p>
        </div>
      )}
      {facts.entry?.attestedAt !== undefined &&
      facts.entry.attestedAt !== null &&
      facts.entry.status === "saisi" ? (
        <div className="mb-3 px-5">
          <p
            className="rounded-2xl border border-indigo-100 bg-indigo-50 px-4 py-3 text-xs leading-relaxed text-indigo-700"
            data-testid="saisie-a-viser"
          >
            {chair === "expert" ? t("expert.saisie.aViserTerrain") : t("expert.saisie.aViserBureau")}
          </p>
        </div>
      ) : null}

      <div className="mb-3 px-5">
        <h2 className="mb-2 text-sm font-semibold">{t("expert.saisie.vehicule")}</h2>
        <div
          className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
          data-testid="saisie-identite"
        >
          <Line
            label={t("expert.saisie.immatriculation")}
            value={demand.vehiclePlate ?? t("expert.saisie.nonImmatricule")}
          />
          <Line
            label={t("expert.saisie.kmDeclare")}
            value={fill("expert.feuille.km", { n: formatQuantity(declared) })}
          />
          <Line label={t("expert.saisie.couleur")} value={demand.vehicleColour} />
          <Line
            label={t("expert.saisie.demande")}
            value={
              demand.clientName === null
                ? t("expert.saisie.controleStock")
                : `${demand.clientName} · ${demand.reference}`
            }
          />
        </div>
      </div>

      <div className="mb-3 px-5">
        <div className="space-y-4 rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <p className="text-xs font-medium text-gray-700">{t("expert.saisie.enArrivant")}</p>
          <div data-testid="saisie-carte-grise">
            <p className="text-sm leading-snug text-gray-900">{t("expert.saisie.carteGriseQuestion")}</p>
            <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
              {t("expert.saisie.carteGriseAide")}
            </p>
            <YesNo
              value={carteGriseFound}
              onChange={setCarteGriseFound}
              testId="saisie-carte-grise"
              disabled={frozen}
            />
          </div>
          <div data-testid="saisie-chassis">
            {comparing ? (
              <>
                <p className="text-sm leading-snug text-gray-900">{t("expert.saisie.chassisQuestion")}</p>
                <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
                  {t("expert.saisie.chassisAide")}
                </p>
              </>
            ) : null}
            <input
              value={chassisRead}
              disabled={frozen}
              onChange={(event) => {
                setChassisRead(event.target.value.toUpperCase());
              }}
              placeholder={t("expert.saisie.chassisLu")}
              data-testid="saisie-chassis-lu"
              className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm tabular-nums"
            />
            {sansCarteGrise ? (
              <p
                className="mt-2 inline-flex rounded-xl bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600"
                data-testid="saisie-chassis-sans-objet"
              >
                {t("expert.saisie.sansCarteGrise")}
              </p>
            ) : null}
            {comparing ? (
              <>
                <YesNo
                  value={chassisMatches}
                  onChange={setChassisMatches}
                  testId="saisie-chassis"
                  disabled={frozen}
                />
                {chassisMatches === false ? (
                  <p className="mt-2 text-xs leading-relaxed text-rose-600">
                    {t("expert.saisie.chassisNonConforme")}
                  </p>
                ) : null}
              </>
            ) : null}
          </div>
          {comparing || sansCarteGrise ? (
            <div data-testid="saisie-annee">
              <p className="text-sm leading-snug text-gray-900">{t("expert.saisie.anneeQuestion")}</p>
              {sansCarteGrise ? (
                <p
                  className="mt-2 inline-flex rounded-xl bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600"
                  data-testid="saisie-annee-sans-objet"
                >
                  {t("expert.saisie.sansCarteGrise")}
                </p>
              ) : (
                <>
                  <p
                    className="mt-0.5 text-[11px] leading-relaxed text-gray-400"
                    data-testid="saisie-annee-aide"
                  >
                    {demand.vehicleYearDeclared === null
                      ? t("expert.saisie.anneeAideSansDeclaration")
                      : fill("expert.saisie.anneeAide", { n: String(demand.vehicleYearDeclared) })}
                  </p>
                  <input
                    value={yearRead}
                    disabled={frozen}
                    inputMode="numeric"
                    maxLength={4}
                    onChange={(event) => {
                      setYearRead(event.target.value.replace(/\D/gu, ""));
                    }}
                    placeholder={t("expert.saisie.anneeLue")}
                    data-testid="saisie-annee-lue"
                    className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm tabular-nums"
                  />
                  <YesNo
                    value={yearMatches}
                    onChange={setYearMatches}
                    testId="saisie-annee"
                    disabled={frozen}
                  />
                  {yearMatches === false ? (
                    <p className="mt-2 text-xs leading-relaxed text-rose-600">
                      {t("expert.saisie.anneeNonConforme")}
                    </p>
                  ) : null}
                </>
              )}
            </div>
          ) : null}
          {unregistered ? (
            <div data-testid="saisie-aucune-plaque">
              <p className="text-sm leading-snug text-gray-900">{t("expert.saisie.aucunePlaqueQuestion")}</p>
              <YesNo
                value={plateMatches}
                onChange={setPlateMatches}
                testId="saisie-aucune-plaque"
                disabled={frozen}
              />
              {plateMatches === false ? (
                <>
                  <p
                    className="mt-2 text-[11px] leading-relaxed text-gray-500"
                    data-testid="saisie-plaque-ecart"
                  >
                    {t("expert.saisie.aucunePlaqueEcart")}
                  </p>
                  <input
                    value={plateRead}
                    disabled={frozen}
                    onChange={(event) => {
                      setPlateRead(event.target.value.toUpperCase());
                    }}
                    placeholder={t("expert.saisie.plaqueLue")}
                    data-testid="saisie-plaque-lue"
                    className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm tabular-nums"
                  />
                </>
              ) : null}
            </div>
          ) : (
            <div>
              <p className="text-sm leading-snug text-gray-900">{t("expert.saisie.plaqueQuestion")}</p>
              <p className="mt-0.5 text-[11px] leading-relaxed text-gray-400">
                {t("expert.saisie.plaqueAide")}
              </p>
              <input
                value={plateRead}
                disabled={frozen}
                onChange={(event) => {
                  setPlateRead(event.target.value.toUpperCase());
                }}
                placeholder={t("expert.saisie.plaqueLue")}
                data-testid="saisie-plaque-lue"
                className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm tabular-nums"
              />
              <YesNo
                value={plateMatches}
                onChange={setPlateMatches}
                testId="saisie-plaque"
                disabled={frozen}
              />
            </div>
          )}
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="saisie-compteur">
              {t("expert.saisie.compteur")}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="saisie-compteur"
                value={odometer}
                disabled={frozen}
                inputMode="numeric"
                onChange={(event) => {
                  setOdometer(event.target.value.replace(/\D/gu, ""));
                }}
                placeholder={formatQuantity(declared)}
                data-testid="saisie-compteur"
                className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-base font-medium tabular-nums"
              />
              <span className="shrink-0 text-sm text-gray-400">km</span>
            </div>
            <p className="mt-1 text-[11px] leading-relaxed text-gray-400">
              {fill("expert.saisie.compteurAide", { n: formatQuantity(declared) })}
            </p>
            {gap !== null && gap !== 0 ? (
              <div
                className={`mt-2 rounded-xl px-3.5 py-2.5 ${Math.abs(gap) > 5000 ? "border border-amber-100 bg-amber-50" : "bg-gray-50"}`}
                data-testid="saisie-ecart-compteur"
              >
                <p className="text-xs leading-relaxed text-gray-700 tabular-nums">
                  {fill(gap > 0 ? "expert.saisie.ecartAuDessus" : "expert.saisie.ecartEnDessous", {
                    n: formatQuantity(Math.abs(gap)),
                  })}{" "}
                  {t("expert.saisie.ecartFait")}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {grid.families
        .filter((family) => family.isCheckable)
        .map((family) => {
          const ticked = family.criteria.filter((one) => observed.has(one.code));
          return (
            <div key={family.code} className="mb-3 px-5" data-family={family.code}>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h2 className="text-sm font-semibold">{family.title}</h2>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    ticked.length > 0
                      ? family.isEliminating
                        ? "bg-rose-50 text-rose-600"
                        : "bg-amber-50 text-amber-700"
                      : "text-gray-400"
                  }`}
                  data-testid={`saisie-famille-${family.code}`}
                >
                  {ticked.length > 0
                    ? fill(ticked.length > 1 ? "expert.saisie.nConstats" : "expert.saisie.nConstat", {
                        n: String(ticked.length),
                      })
                    : t("expert.saisie.rienASignaler")}
                </span>
              </div>
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
                <p className="border-b border-gray-100 px-4 py-2.5 text-xs leading-relaxed text-gray-500">
                  {family.helpText}
                </p>
                <ul className="divide-y divide-gray-50">
                  {family.criteria.map((criterion) => {
                    const active = observed.has(criterion.code);
                    return (
                      <li key={criterion.code} className="px-4 py-3">
                        <button
                          type="button"
                          data-criterion={criterion.code}
                          data-testid={`saisie-critere-${criterion.code}`}
                          aria-pressed={active}
                          disabled={frozen}
                          onClick={() => {
                            toggle(criterion.code);
                          }}
                          className="flex w-full items-start gap-3 text-left"
                        >
                          <span
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                              active
                                ? family.isEliminating
                                  ? "border-rose-500 bg-rose-500"
                                  : "border-amber-500 bg-amber-500"
                                : "border-gray-300"
                            }`}
                          >
                            {active ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-white" aria-hidden="true" />
                            ) : null}
                          </span>
                          <span
                            className={`text-sm leading-snug ${active ? "font-medium text-gray-900" : "text-gray-700"}`}
                          >
                            {criterion.label}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setHelp(help === criterion.code ? null : criterion.code);
                          }}
                          className="ml-8 mt-1 text-xs font-medium text-indigo-600"
                        >
                          {help === criterion.code
                            ? t("expert.saisie.masquer")
                            : t("expert.saisie.aideConstat")}
                        </button>
                        {help === criterion.code ? (
                          <p className="ml-8 mt-1.5 rounded-xl bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
                            {criterion.helpText}
                          </p>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          );
        })}

      <div className="mb-3 px-5">
        <h2 className="mb-2 text-sm font-semibold">{t("expert.saisie.valorisation")}</h2>
        <div className="space-y-3 rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <p className="text-xs leading-relaxed text-gray-500">{t("expert.saisie.valorisationAide")}</p>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="saisie-valeur">
              {t("expert.saisie.valeur")}
            </label>
            <div className="flex items-center gap-2">
              <input
                id="saisie-valeur"
                value={value}
                disabled={frozen}
                inputMode="numeric"
                onChange={(event) => {
                  setValue(event.target.value.replace(/\D/gu, ""));
                }}
                data-testid="saisie-valeur"
                className="flex-1 rounded-xl border border-gray-200 px-3 py-2.5 text-base font-medium tabular-nums"
              />
              <span className="shrink-0 text-sm text-gray-400">{t("common.monnaie.fcfa")}</span>
            </div>
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="saisie-observations">
              {t("expert.saisie.observations")}
            </label>
            <textarea
              id="saisie-observations"
              rows={4}
              value={observations}
              disabled={frozen}
              onChange={(event) => {
                setObservations(event.target.value);
              }}
              placeholder={t("expert.saisie.observationsAide")}
              data-testid="saisie-observations"
              className="w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="mb-3 px-5">
        <h2 className="mb-2 text-sm font-semibold">{t("expert.saisie.photos")}</h2>
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <p className="mb-3 text-xs leading-relaxed text-gray-500">{t("expert.saisie.photosAide")}</p>
          <div className="grid grid-cols-4 gap-2">
            {EXPERT_VIEWS.map(({ key: view, label }) => {
              const taken = photos.find((one) => one.view === view);
              return (
                <label
                  key={view}
                  data-view={view}
                  data-testid={`saisie-vue-${view}`}
                  data-taken={taken === undefined ? "non" : "oui"}
                  className={`cursor-pointer rounded-lg border px-1 py-2 text-center ${
                    taken === undefined
                      ? "border-dashed border-gray-300 bg-white"
                      : "border-indigo-200 bg-indigo-50"
                  }`}
                >
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    disabled={frozen}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file !== undefined) attach(view, file);
                    }}
                  />
                  <span className="flex h-7 items-center justify-center">
                    {taken === undefined ? (
                      <Plus className="h-4 w-4 text-gray-300" aria-hidden="true" />
                    ) : (
                      <CheckCircle2 className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                    )}
                  </span>
                  <span
                    className={`block text-[10px] leading-tight ${taken === undefined ? "text-gray-500" : "font-medium text-indigo-700"}`}
                  >
                    {t(label)}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="mt-2 text-[10px] leading-relaxed text-gray-400">{t("expert.saisie.photosNote")}</p>
        </div>
      </div>

      <div className="px-5">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4">
          <button
            type="button"
            disabled={frozen}
            onClick={() => {
              setAttested(!attested);
            }}
            data-testid="saisie-attestation"
            aria-pressed={attested}
            className="flex w-full items-start gap-2.5 text-left"
          >
            <span
              className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                attested ? "border-indigo-600 bg-indigo-600" : "border-gray-300"
              }`}
            >
              {attested ? <CheckCircle2 className="h-3.5 w-3.5 text-white" aria-hidden="true" /> : null}
            </span>
            <span className="text-xs leading-relaxed text-gray-700">
              {grid.families
                .filter(
                  (family) => family.isCheckable && !family.criteria.some((one) => observed.has(one.code)),
                )
                .map((family) => fill("expert.saisie.aucunConstat", { family: family.title }))
                .join(" ")}{" "}
              {t("expert.saisie.attestation")}
            </span>
          </button>
          <div className="mt-3 rounded-xl bg-gray-50 px-3.5 py-3">
            <p className="text-xs leading-relaxed text-gray-600">{t("expert.saisie.grilleDecide")}</p>
          </div>
        </div>
      </div>

      {sent && !readOnly ? (
        <div className="sticky bottom-0 bg-gray-50 px-5 pb-2 pt-3">
          <div
            role="status"
            data-testid="saisie-transmise"
            className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 text-emerald-800"
          >
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" aria-hidden="true" />
            <div>
              <p className="text-base font-semibold leading-snug">{t("expert.saisie.bandeauTransmise")}</p>
              <p className="mt-0.5 text-sm tabular-nums">
                {fill("expert.saisie.bandeauHeure", { heure: formatDateTime(new Date(sentAt)) })}
              </p>
            </div>
          </div>
          {word !== null && wordRefused ? (
            <p
              role="alert"
              className="mt-2 text-center text-xs font-medium text-rose-600"
              data-testid="saisie-mot"
            >
              {word}
            </p>
          ) : null}
        </div>
      ) : frozen ? null : (
        <div className="sticky bottom-0 bg-gray-50 px-5 pb-2 pt-3">
          <button
            type="button"
            disabled={guard !== null || busy}
            onClick={() => {
              save(true);
            }}
            data-testid="saisie-envoyer"
            className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:bg-indigo-200 disabled:text-indigo-900"
          >
            {t("expert.saisie.transmettre")}
            {count > 0
              ? ` · ${fill(count > 1 ? "expert.saisie.nConstats" : "expert.saisie.nConstat", { n: String(count) })}`
              : ""}
            {guard === null ? null : (
              <span className="mt-1 block px-3 text-xs font-normal leading-snug" data-testid="saisie-garde">
                {guard}
              </span>
            )}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              save(false);
            }}
            data-testid="saisie-garder"
            className="mt-2 w-full rounded-2xl border border-gray-200 bg-white py-2.5 text-xs font-medium text-gray-600"
          >
            {t("expert.saisie.garder")}
          </button>
          {guard === null ? (
            <p className="mt-2 text-center text-xs leading-snug text-gray-400">
              {t("expert.saisie.gardeeSurAppareil")}
            </p>
          ) : null}
          {word === null ? null : (
            <p
              role={wordRefused ? "alert" : "status"}
              className={`mt-2 text-center text-xs ${wordRefused ? "font-medium text-rose-600" : "text-gray-600"}`}
              data-testid="saisie-mot"
            >
              {word}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
